import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from '@prody/shared';
import en from './locales/en';

/* ------------------------------------------------------------------ *
 * i18n
 *
 * English is bundled; Spanish and Russian are code-split and fetched
 * on demand, so a Spanish tenant does not download Cyrillic strings.
 *
 * Two deliberate decisions:
 *
 * 1. The UI language and the formatting locale are separate. A Russian
 *    speaker running a business in Barcelona wants the interface in
 *    Russian and the numbers as "1.234,56 €". Formatting comes from
 *    `business.settings.formatLocale`, never from i18next.
 *
 * 2. There is a fourth locale, `pseudo`, generated at runtime in dev.
 *    It inflates every string by 40% and brackets it, so overflow is
 *    caught on a laptop rather than by a Spanish-speaking customer.
 *    Enable with ?locale=pseudo.
 * ------------------------------------------------------------------ */

const loaders: Record<Exclude<Locale, 'en'>, () => Promise<{ default: unknown }>> = {
  es: () => import('./locales/es'),
  ru: () => import('./locales/ru'),
};

export const NAMESPACES = Object.keys(en);

/** ASCII-only transform so the inflated text stays legible while clearly fake. */
const pseudoize = (value: unknown): unknown => {
  if (typeof value === 'string') {
    // Leave interpolation placeholders intact or nothing renders.
    const padded = value.replace(/([^{}]+)/g, (chunk) =>
      chunk.trim() ? `${chunk}${'·'.repeat(Math.ceil(chunk.length * 0.4))}` : chunk,
    );
    return `⟦${padded}⟧`;
  }
  if (Array.isArray(value)) return value.map(pseudoize);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, pseudoize(v)]),
    );
  }
  return value;
};

export const initI18n = async (initialLocale?: Locale) => {
  await i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: { en: en as unknown as Record<string, object> },
      ...(initialLocale ? { lng: initialLocale } : {}),
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES, 'pseudo'],
      ns: NAMESPACES,
      defaultNS: 'common',
      interpolation: { escapeValue: false },
      detection: {
        order: ['querystring', 'localStorage', 'navigator'],
        lookupQuerystring: 'locale',
        lookupLocalStorage: 'prody.locale',
        caches: ['localStorage'],
      },
      returnNull: false,
    });

  if (import.meta.env.DEV) {
    const inflated = pseudoize(en) as Record<string, object>;
    for (const ns of NAMESPACES) {
      i18next.addResourceBundle('pseudo', ns, inflated[ns] ?? {});
    }
  }

  /*
   * Deliberately `i18next.language`, not `resolvedLanguage`.
   *
   * Only English is bundled. At init the detector has already picked,
   * say, `ru` — but because no `ru` resources exist yet, i18next
   * *resolves* to the `en` fallback. Keying off the resolved value
   * therefore skips the load and leaves every Spanish and Russian user
   * looking at an English app, silently, forever. `language` holds
   * what was actually detected, so we load that bundle and then switch
   * for real.
   */
  const detected = (i18next.language ?? '').split('-')[0] as Locale | undefined;
  if (detected && detected !== 'en' && detected in loaders) {
    await loadLocale(detected);
    await i18next.changeLanguage(detected);
  }

  return i18next;
};

const loaded = new Set<string>(['en']);

export const loadLocale = async (locale: Locale): Promise<void> => {
  if (loaded.has(locale) || locale === 'en') return;
  const loader = loaders[locale as Exclude<Locale, 'en'>];
  if (!loader) return;
  const bundle = (await loader()).default as Record<string, object>;
  for (const [ns, resources] of Object.entries(bundle)) {
    i18next.addResourceBundle(locale, ns, resources, true, true);
  }
  loaded.add(locale);
};

export const changeLocale = async (locale: Locale): Promise<void> => {
  await loadLocale(locale);
  await i18next.changeLanguage(locale);
  document.documentElement.lang = locale;
};

export default i18next;
